import { JACKPOT_NUMBER, NUMBER_MAX, NUMBER_MIN } from './constants.js';
import type { PricingMode } from './pricing.js';

/**
 * Game families. Derived from v1 game types (seed.sql `game_types`) collapsed
 * into their settlement behaviour:
 *  - jackpot : wins only when the winning number is 0.
 *  - exact   : the "number" game — punter picks specific numbers (1..38); wins
 *              if the winning number is one of them.
 *  - range   : high/low and the quadrant games (1-9, 10-19, 20-29, 30-38) —
 *              wins if the winning number falls in a contiguous band.
 *  - parity  : odd/even — wins on the winning number's parity.
 */
export type GameKind = 'jackpot' | 'exact' | 'range' | 'parity';

export interface GameRule {
  kind: GameKind;
  /** Inclusive lower bound, for kind 'range'. */
  rangeMin?: number;
  /** Inclusive upper bound, for kind 'range'. */
  rangeMax?: number;
  /** Required parity, for kind 'parity'. */
  parity?: 'odd' | 'even';
  /**
   * Whether the jackpot number (0) is excluded from a win.
   *
   * Derived from v1 constants/index.js GAME_NUMBERS: only `jackpot` lists 0.
   * high/low/odd/even and every quadrant band OMIT 0, so a jackpot draw (0)
   * never satisfies a range/parity/exact ticket — only a jackpot ticket wins.
   * Note this makes 0 count as neither odd nor even for settlement, even though
   * 0 is mathematically even.
   */
  excludesZero: boolean;
}

/**
 * The v1 game-type names, in seed.sql order. Kept as a readonly tuple so the
 * sale schema can build a zod enum from the exact same source.
 */
export const V1_GAME_NAMES = [
  'jackpot',
  'number',
  'high',
  'low',
  'odd',
  'even',
  '1-9',
  '10-19',
  '20-29',
  '30-38',
] as const;

export type V1GameName = (typeof V1_GAME_NAMES)[number];

/**
 * v1 game-type name → settlement rule.
 *
 * Bands are taken verbatim from v1 constants/index.js GAME_NUMBERS:
 *   high  = 20..38   low = 1..19
 *   1-9   = 1..9     10-19 = 10..19   20-29 = 20..29   30-38 = 30..38
 *   odd   = 1,3,..,37   even = 2,4,..,38   (both exclude 0)
 *   number (exact) = 1..38 (excludes 0; 0 is jackpot-only)
 */
export const V1_GAME_RULES: Record<string, GameRule> = {
  jackpot: { kind: 'jackpot', excludesZero: false },
  number: { kind: 'exact', excludesZero: true },
  high: { kind: 'range', rangeMin: 20, rangeMax: 38, excludesZero: true },
  low: { kind: 'range', rangeMin: 1, rangeMax: 19, excludesZero: true },
  odd: { kind: 'parity', parity: 'odd', excludesZero: true },
  even: { kind: 'parity', parity: 'even', excludesZero: true },
  '1-9': { kind: 'range', rangeMin: 1, rangeMax: 9, excludesZero: true },
  '10-19': { kind: 'range', rangeMin: 10, rangeMax: 19, excludesZero: true },
  '20-29': { kind: 'range', rangeMin: 20, rangeMax: 29, excludesZero: true },
  '30-38': { kind: 'range', rangeMin: 30, rangeMax: 38, excludesZero: true },
};

/**
 * v1 odds per game type, expressed as integer hundredths of a multiplier
 * (matching the minor-unit/hundredths convention used for money).
 *
 * Source: seed.sql `game_odds` (DECIMAL(8,2)): jackpot 40, number 30,
 * high/low/odd/even 1, quadrants 2. These are the seed defaults; the running
 * shop keeps live odds in its own `game_odds`/config, so treat this as the v1
 * baseline, not the runtime authority.
 */
export const V1_GAME_ODDS_HUNDREDTHS: Record<string, number> = {
  jackpot: 4000,
  number: 3000,
  high: 100,
  low: 100,
  odd: 100,
  even: 100,
  '1-9': 200,
  '10-19': 200,
  '20-29': 200,
  '30-38': 200,
};

/**
 * v1-CURRENT client pricing mode per game type (BLUEPRINT §4).
 *
 * jackpot and number(exact) charge subtotal = bet × count-of-numbers, so they
 * are 'per-number'.
 *
 * The 8 band games (high/low/odd/even + the four quadrant games) are seeded
 * 'flat' because v1's CURRENT client lists them in FIXED_COST_GAME_TYPES and
 * charges a flat subtotal = bet.
 *
 * ⚠ THE 8 BAND VALUES ARE PROVISIONAL — PENDING AN OWNER DECISION. Older
 * production dumps show these same games once charged bet × band-size
 * ('per-number'). Because the two eras disagree, the pricing mode is stored as
 * per-game-type DATA (game_types.pricing_mode), NOT baked into code, so the
 * owner can flip any band game to 'per-number' without a code change. This map
 * is only the seed the migration installs.
 */
export const V1_GAME_PRICING_MODES: Record<string, PricingMode> = {
  jackpot: 'per-number',
  number: 'per-number',
  high: 'flat', // PROVISIONAL — owner decision pending
  low: 'flat', // PROVISIONAL — owner decision pending
  odd: 'flat', // PROVISIONAL — owner decision pending
  even: 'flat', // PROVISIONAL — owner decision pending
  '1-9': 'flat', // PROVISIONAL — owner decision pending
  '10-19': 'flat', // PROVISIONAL — owner decision pending
  '20-29': 'flat', // PROVISIONAL — owner decision pending
  '30-38': 'flat', // PROVISIONAL — owner decision pending
};

function inNumberRange(n: number): boolean {
  return Number.isInteger(n) && n >= NUMBER_MIN && n <= NUMBER_MAX;
}

/**
 * Does a ticket win, given its rule, its selected numbers and the draw's
 * winning number?
 *
 * Mirrors v1 settlement (draws.js): a ticket wins iff its stored selection set
 * contains the winning number. In v1 every game type materialised its full set
 * of numbers in `ticket_numbers` (validateNumbersForGameType enforced the exact
 * set for range/parity), so range/parity win purely by band/parity — v2 stores
 * no selection rows for those (BLUEPRINT §4) and reconstructs the test here.
 *
 * `selections` is consulted only for the 'exact' game; for jackpot/range/parity
 * the outcome is a pure function of the rule and the winning number.
 */
export function doesTicketWin(
  rule: GameRule,
  selections: readonly number[],
  winningNumber: number,
): boolean {
  if (!inNumberRange(winningNumber)) return false;
  if (rule.excludesZero && winningNumber === JACKPOT_NUMBER) return false;

  switch (rule.kind) {
    case 'jackpot':
      return winningNumber === JACKPOT_NUMBER;

    case 'exact':
      // Exact "number" ticket: win iff the winning number was picked.
      return selections.includes(winningNumber);

    case 'range': {
      if (rule.rangeMin === undefined || rule.rangeMax === undefined) {
        return false;
      }
      return winningNumber >= rule.rangeMin && winningNumber <= rule.rangeMax;
    }

    case 'parity': {
      const isOdd = winningNumber % 2 === 1;
      return rule.parity === 'odd' ? isOdd : !isOdd;
    }
  }
}
